/**
 * Send-to-PACS proposal card (the runtime HITL gate) for
 * `send_structure_set_to_pacs`. Pure presentational: it shows the structure
 * set, the destination PACS and the structures to send; nothing leaves the
 * viewer until the clinician presses Apply, which runs the same send as the
 * Export dialog. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import ProposalCardShell, {
    ProposalSummaryBox,
    type ProposalStatus,
} from './ProposalCardShell';

/** Names listed on the card before "and N more". */
const MAX_NAMES_SHOWN = 8;

export interface SendStructureSetProposalCardProps {
    readonly structureSetLabel: string;
    readonly targetName: string;
    readonly structureNames: ReadonlyArray<string>;
    readonly isDefaultSelection: boolean;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function SendStructureSetProposalCard({
    structureSetLabel,
    targetName,
    structureNames,
    isDefaultSelection,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: SendStructureSetProposalCardProps) {
    const shown = structureNames.slice(0, MAX_NAMES_SHOWN);
    const more = structureNames.length - shown.length;
    return (
        <ProposalCardShell
            title="Send to PACS"
            ariaLabel="Send structure set to PACS proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <ProposalSummaryBox>
                send “{structureSetLabel}” → <strong>{targetName}</strong>
                <br />
                {structureNames.length} structure
                {structureNames.length === 1 ? '' : 's'}: {shown.join(', ')}
                {more > 0 ? ` and ${more} more` : ''}
            </ProposalSummaryBox>
            <Typography variant="caption" color="text.secondary">
                Same as Export to PACS.
                {isDefaultSelection
                    ? ' Structures: the Export dialog default (approved ones if any, otherwise all not rejected).'
                    : ''}{' '}
                Data leaves the viewer only after Apply.
            </Typography>
        </ProposalCardShell>
    );
}
